/**
 * Hole Cards View for Poker Reborn
 *
 * Wrapper component that manages two different card layouts:
 * - HeroCardsLayout: For the hero player (medium cards, prominent)
 * - OpponentCardsLayout: For opponents (small cards, compact)
 *
 * Only one layout is active at a time. Use setLayoutMode() to switch.
 *
 * @see docs/screens/room/CARDS_HERO.md - Visual specs
 * @see docs/screens/room/CARD_VIEW.md - CardView component reference
 */

import { Container } from 'pixi.js';
import { HeroCardsLayout } from './HeroCardsLayout';
import { OpponentCardsLayout } from './OpponentCardsLayout';
import type { Card } from '@/types/game';
import { addOriginMarker, ORIGIN_COLORS } from '@/utils/OriginDebug';

// =============================================================================
// TYPES
// =============================================================================

export type HoleCardsLayoutMode = 'hero' | 'opponent';

// =============================================================================
// HOLE CARDS VIEW (WRAPPER)
// =============================================================================

export class HoleCardsView extends Container {
  private heroLayout: HeroCardsLayout;
  private opponentLayout: OpponentCardsLayout;
  private currentMode: HoleCardsLayoutMode;

  constructor(initialMode: HoleCardsLayoutMode = 'opponent', seatIndex = 0) {
    super();
    this.label = 'holeCardsView';

    // Create both layouts
    this.heroLayout = new HeroCardsLayout();
    this.opponentLayout = new OpponentCardsLayout(seatIndex);

    // Add both to container (only one visible)
    this.addChild(this.heroLayout);
    this.addChild(this.opponentLayout);

    // Set initial mode
    this.currentMode = initialMode;
    this.heroLayout.visible = initialMode === 'hero';
    this.opponentLayout.visible = initialMode === 'opponent';

    // Debug: Add origin marker if ?showOrigins=true
    addOriginMarker(this, ORIGIN_COLORS.holeCards);

    // Start hidden
    this.visible = false;
  }

  // ===========================================================================
  // LAYOUT MODE
  // ===========================================================================

  /**
   * Get current layout mode
   */
  getLayoutMode(): HoleCardsLayoutMode {
    return this.currentMode;
  }

  /**
   * Set layout mode (hero or opponent)
   * Transfers card state when switching
   */
  setLayoutMode(mode: HoleCardsLayoutMode): void {
    if (mode === this.currentMode) return;

    // Get state from current layout
    const currentLayout = this.getActiveLayout();
    const state = currentLayout.getState();

    // Switch mode
    this.currentMode = mode;
    this.heroLayout.visible = mode === 'hero';
    this.opponentLayout.visible = mode === 'opponent';

    // Transfer state to new layout
    const newLayout = this.getActiveLayout();
    newLayout.restoreState(state);
  }

  /**
   * Get the currently active layout
   */
  private getActiveLayout(): HeroCardsLayout | OpponentCardsLayout {
    return this.currentMode === 'hero' ? this.heroLayout : this.opponentLayout;
  }

  // ===========================================================================
  // PUBLIC API - DELEGATED TO ACTIVE LAYOUT
  // ===========================================================================

  /**
   * Deal cards with animation
   * @param cards - The two hole cards
   * @param faceUp - Whether to flip face up after dealing
   * @param fromPosition - Optional start position in local coordinates (default: above component)
   */
  async deal(cards: Card[], faceUp: boolean, fromPosition?: { x: number; y: number }): Promise<void> {
    this.visible = true;
    await this.getActiveLayout().deal(cards, faceUp, fromPosition);
  }

  /**
   * Reveal cards (flip face up)
   */
  async reveal(cards: Card[]): Promise<void> {
    this.visible = true;
    await this.getActiveLayout().reveal(cards);
  }

  /**
   * Show cards at showdown with dramatic flourish (hero only)
   * Cards spread apart, lift up, and scale up.
   */
  async show(): Promise<void> {
    this.visible = true;
    if (this.currentMode === 'hero') {
      await this.heroLayout.show();
    }
    // Opponents don't have a show animation - they use reveal
  }

  /**
   * Fold cards - animate to muck
   */
  async fold(): Promise<void> {
    await this.getActiveLayout().fold();
  }

  /**
   * Muck cards - animate cards flying to muck pile (hero only)
   * Used when player chooses not to show at showdown.
   */
  async muck(): Promise<void> {
    if (this.currentMode === 'hero') {
      await this.heroLayout.muck();
    } else {
      // Opponents use fold animation for muck
      await this.opponentLayout.fold();
    }
  }

  /**
   * Set cards without animation
   */
  setCards(cards: Card[] | null, faceUp = true): void {
    if (!cards) {
      this.visible = false;
      return;
    }

    this.visible = true;
    this.getActiveLayout().setCards(cards, faceUp);
  }

  /**
   * Show card backs (opponent's cards) - no animation
   */
  showBacks(): void {
    this.visible = true;
    this.getActiveLayout().showBacks();
  }

  /**
   * Check if cards are currently revealed (face-up)
   * Used to prevent flipping revealed cards back to face-down
   */
  isRevealed(): boolean {
    const state = this.getActiveLayout().getState();
    return state.card1FaceUp || state.card2FaceUp;
  }

  /**
   * Get internal card state (for refresh/recovery)
   */
  getState(): { card1: Card | null; card2: Card | null; card1FaceUp: boolean; card2FaceUp: boolean } {
    return this.getActiveLayout().getState();
  }

  /**
   * Check if the view has any cards (regardless of game state)
   */
  hasCardsInView(): boolean {
    const state = this.getActiveLayout().getState();
    return state.card1 !== null || state.card2 !== null;
  }

  /**
   * Set hole card count (2 for Hold'em, 4 for Omaha)
   * Must be called before dealing to rebuild card slots.
   */
  setCardCount(cardCount: number): void {
    this.heroLayout.setCardCount(cardCount);
    this.opponentLayout.setCardCount(cardCount);
  }

  /**
   * Deal card backs with animation (for opponents)
   * @param fromPosition - Optional start position in local coordinates (default: above component)
   */
  async dealBacks(fromPosition?: { x: number; y: number }): Promise<void> {
    this.visible = true;
    await this.getActiveLayout().dealBacks(fromPosition);
  }

  /**
   * Deal a single card back (for opponents in round-robin dealing)
   * @param cardIndex Card index (0-based)
   * @param fromPosition Starting position (deck location)
   */
  async dealSingleCardBack(
    cardIndex: number,
    fromPosition?: { x: number; y: number }
  ): Promise<void> {
    this.visible = true;
    await this.opponentLayout.dealSingleCardBack(cardIndex, fromPosition);
  }

  /**
   * Deal a single card with data (for hero in round-robin dealing)
   * @param cardIndex Card index (0-based)
   * @param card The card data to display
   * @param fromPosition Starting position (deck location)
   */
  async dealSingleCard(
    cardIndex: number,
    card: Card,
    fromPosition?: { x: number; y: number }
  ): Promise<void> {
    this.visible = true;
    await this.heroLayout.dealSingleCard(cardIndex, card, fromPosition);
  }

  /**
   * Hide cards
   */
  hide(): void {
    // Reset any highlights before hiding
    this.resetHighlights();
    this.visible = false;
  }

  /**
   * Highlight cards (winning hand) with glow and pulse effect
   */
  highlight(on: boolean): void {
    this.getActiveLayout().highlight(on);
  }

  /**
   * Highlight specific cards if they match winning cards.
   * Core cards get strong gold highlight, kicker cards get subtle gray highlight.
   */
  highlightWinningCards(coreCards: Card[], kickerCards: Card[] = []): void {
    this.getActiveLayout().highlightWinningCards(coreCards, kickerCards);
  }

  /**
   * Reset all highlights
   */
  resetHighlights(): void {
    // Reset both layouts to be safe
    this.heroLayout.resetHighlights();
    this.opponentLayout.resetHighlights();
  }

  /**
   * Force redraw of card graphics after GPU context loss.
   * @see docs/systems/BACKGROUND_TAB_RECOVERY.md
   */
  redraw(): void {
    this.heroLayout.redraw();
    this.opponentLayout.redraw();
  }

  /**
   * Nuclear reset - restore ALL card properties to pristine state.
   * Called at hand start to ensure clean slate.
   */
  resetAllCards(): void {
    // Reset both layouts (nuclear reset on all cards)
    this.heroLayout.resetAllCards();
    this.opponentLayout.resetAllCards();

    // Reset layout visibility based on current mode
    // (animateFold sets layout.visible = false, so we need to restore it)
    this.heroLayout.visible = this.currentMode === 'hero';
    this.opponentLayout.visible = this.currentMode === 'opponent';

    this.visible = false;
  }

  /**
   * Darken cards (losing hand)
   */
  darken(on: boolean): void {
    this.getActiveLayout().darken(on);
  }

  // ===========================================================================
  // RESPONSIVE SCALING
  // ===========================================================================

  /**
   * Update layout for screen size (responsive scaling)
   * @param screenWidth - Current screen width in pixels
   */
  updateLayout(screenWidth: number): void {
    this.heroLayout.updateLayout(screenWidth);
    this.opponentLayout.updateLayout(screenWidth);
  }

  // ===========================================================================
  // LAYOUT ACCESS (for direct manipulation if needed)
  // ===========================================================================

  /**
   * Get the hero cards layout
   */
  getHeroLayout(): HeroCardsLayout {
    return this.heroLayout;
  }

  /**
   * Get the opponent cards layout
   */
  getOpponentLayout(): OpponentCardsLayout {
    return this.opponentLayout;
  }

  // ===========================================================================
  // DEPRECATED - For backwards compatibility
  // ===========================================================================

  /**
   * @deprecated Use setLayoutMode('hero') or setLayoutMode('opponent') instead
   */
  setCardSize(size: 'small' | 'medium' | 'large'): void {
    // Map old size to new mode
    if (size === 'medium' || size === 'large') {
      this.setLayoutMode('hero');
    } else {
      this.setLayoutMode('opponent');
    }
  }

  /**
   * @deprecated Use getLayoutMode() instead
   */
  getCardSize(): 'small' | 'medium' {
    return this.currentMode === 'hero' ? 'medium' : 'small';
  }
}
